import { Badge, Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import {
  IconBook2,
  IconCards,
  IconCheck,
  IconDice,
  IconEye,
  IconHelpCircle,
  IconPlayerPlay,
  IconSparkles,
  IconArmchair,
  IconWifiOff,
} from '@tabler/icons-react';
import {
  DEMO_SOLO_TASK_REWARDS,
  type EventCardAward,
  type GameSessionView,
} from '@creativitas/shared';
import type { RealtimeStatus } from '@/features/spreads/useSessionRealtime';
import { AwardMenu } from '../GameCardsPanel';
import { demoGuidance, type DemoAction, type DemoStepView, type DemoViewer } from './demo-step';

const ACTION_ICONS: Record<DemoAction['kind'], React.ReactNode> = {
  start: <IconPlayerPlay size={18} />,
  seat: <IconArmchair size={18} />,
  open_story: <IconBook2 size={18} />,
  close_story: <IconBook2 size={18} />,
  start_talent: <IconSparkles size={18} />,
  draw: <IconCards size={18} />,
  reveal: <IconEye size={18} />,
  confirm: <IconCheck size={18} />,
  resolve: <IconCheck size={18} />,
  roll: <IconDice size={18} />,
};

/**
 * Панель хода в демо (ТЗ демо, п. 7.3): что происходит, кто действует и одно
 * следующее действие. Пояснение стоит рядом с кнопкой, а не в подсказке
 * обучения, — его видно и после «Пропустить обучение».
 */
export function DemoTurnPanel({
  session,
  viewer,
  step,
  busy,
  connection,
  onAction,
  onResolve,
  onReplayCoach,
}: {
  session: GameSessionView;
  viewer: DemoViewer;
  step: DemoStepView;
  busy: boolean;
  connection?: RealtimeStatus;
  onAction: (action: DemoAction) => void;
  onResolve?: (cardId: string, award: EventCardAward) => void;
  onReplayCoach: () => void;
}) {
  /*
    Связь потеряна: последнее состояние остаётся на экране, а команды ждут.
    Восстановившись, сокет сам получает свежий снимок с сервера, и ничего из
    сделанного не повторяется.
  */
  const offline = connection === 'disconnected' || connection === 'error';
  const action = step.action;
  const dice = session.lastDice;
  const stoppedAt = dice?.stoppedAtSectorId
    ? session.game.board?.sectors.find((sector) => sector.id === dice.stoppedAtSectorId)?.title
    : null;
  const roller = session.participants.find((item) => item.id === dice?.participantId);

  return (
    <Paper withBorder p="md" data-testid="demo-turn">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <div>
            <Text size="xs" tt="uppercase" fw={700} c="var(--accent)">Сейчас</Text>
            <Title order={3} data-testid="demo-step-headline">{step.headline}</Title>
          </div>
          {dice ? (
            <Badge size="xl" variant="light" color="gold" data-testid="last-dice" title="Последний бросок">
              {dice.value}
            </Badge>
          ) : null}
        </Group>

        <DemoRouteProgress session={session} viewer={viewer} />

        <Text size="sm" data-testid="demo-step-detail">{step.detail}</Text>

        {dice && roller ? (
          <Text size="xs" c="var(--text-muted)" data-testid="demo-last-move">
            {roller.displayName}: выпало {dice.value}, клетка {dice.from + 1} → {dice.to + 1}
            {stoppedAt && dice.burnedPips
              ? `. Фишка остановилась на последней клетке сектора «${stoppedAt}», лишние очки сгорели: ${dice.burnedPips}.`
              : ''}
          </Text>
        ) : null}

        {action?.kind === 'resolve' && onResolve ? (
          <div data-coach={step.coach ?? undefined} style={{ alignSelf: 'flex-start' }}>
            <AwardMenu
              size="md"
              busy={busy || offline}
              recommended={action.sectorId ? DEMO_SOLO_TASK_REWARDS[action.sectorId] : undefined}
              onAward={(award) => onResolve(action.cardId, award)}
            />
          </div>
        ) : action ? (
          <Button
            size="md"
            leftSection={ACTION_ICONS[action.kind]}
            disabled={busy || offline}
            loading={busy}
            onClick={() => onAction(action)}
            data-coach={step.coach ?? undefined}
            data-testid="demo-primary-action"
          >
            {step.actionLabel}
          </Button>
        ) : null}

        {offline ? (
          <Group gap={6} wrap="nowrap" data-testid="demo-offline">
            <IconWifiOff size={15} color="var(--gold-deep)" />
            <Text size="xs" c="var(--gold-deep)">
              Связь восстанавливается. Состояние партии сохранено; действия вернутся, как только
              придёт актуальное состояние с сервера.
            </Text>
          </Group>
        ) : null}

        <Group justify="flex-end">
          <Button
            size="compact-xs"
            variant="subtle"
            color="gray"
            leftSection={<IconHelpCircle size={14} />}
            onClick={onReplayCoach}
          >
            Показать подсказки снова
          </Button>
        </Group>
      </Stack>
    </Paper>
  );
}

/** Маяк → Дракон → Замок и ключ: какие сектора уже закрыты действием карты. */
export function DemoRouteProgress({ session, viewer }: { session: GameSessionView; viewer: DemoViewer }) {
  const demo = session.demo;
  if (!demo) return null;
  const subjectId = viewer.participantId ?? demo.nextStep.participantId ??
    demo.progress[0]?.participantId ?? null;
  const progress = demo.progress.find((item) => item.participantId === subjectId);
  const titleOf = (id: string) =>
    session.game.board?.sectors.find((sector) => sector.id === id)?.title ?? id;

  return (
    <Group gap={6} wrap="wrap" data-testid="demo-route">
      {demo.routeSectorIds.map((sectorId, index) => {
        const done = progress?.completedSectorIds.includes(sectorId) ?? false;
        return (
          <Group key={sectorId} gap={6} wrap="nowrap">
            {index > 0 ? <Text size="xs" c="var(--text-subtle)">→</Text> : null}
            <Badge
              variant={done ? 'filled' : 'light'}
              color={done ? 'teal' : 'gray'}
              leftSection={done ? <IconCheck size={12} /> : null}
              data-done={done}
            >
              {titleOf(sectorId)}
            </Badge>
          </Group>
        );
      })}
    </Group>
  );
}

/**
 * Методическое пояснение к текущему сектору. Игроку одиночной партии — что
 * сделать самому; ведущему — как работать с участником. Гостям группы
 * пояснения ведущего не показываются.
 */
export function DemoGuidance({ session, viewer }: { session: GameSessionView; viewer: DemoViewer }) {
  const guidance = demoGuidance(session, viewer);
  if (!guidance) return null;

  return (
    <Paper withBorder p="md" data-testid="demo-guidance" style={{ background: 'var(--surface-elevated)' }}>
      <Stack gap={6}>
        <Text size="xs" tt="uppercase" fw={700} c="var(--gold-deep)">
          {viewer.role === 'solo' ? 'Для размышления' : 'Подсказка ведущему'}
        </Text>
        {guidance.intro ? <Text size="sm">{guidance.intro}</Text> : null}
        <Text size="sm" c={guidance.intro ? 'var(--text-muted)' : undefined} data-stage={guidance.stage}>
          {guidance.text}
        </Text>
      </Stack>
    </Paper>
  );
}
